'use client';

import { c as _c } from "react/compiler-runtime";
import { type SearchFactiii } from '@shared/all/domains/factiiis';
import { postDataSort } from '@shared/all/domains/posts';
import { DeploymentLayout } from '@shared/all/domains/spaces';
import { PreferencesEnum } from '@shared/all/domains/user';
import AuthWidgetContext from '@shared/utils/context/AuthWidgetContext';
import useFactiiis from '@shared/utils/hooks/useFactiiis';
import useLocalPreference from '@shared/utils/hooks/useLocalPreference';
import usePreference from '@shared/utils/hooks/usePreference';
import useUser from '@shared/utils/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, ChevronDown, FileText, RotateCcw, Search, Settings } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useContext, useEffect, useRef, useState } from 'react';
import { useInstanceSpaceSlug } from '../../../../hooks/useInstanceSpaceSlug';
import usePresence from '../../../../hooks/usePresence';
import { animDelay } from '../../../../utils/animDelay';
import { spaceConfig } from '../../../../utils/getSpaceConfig';
import { client } from '../../../../utils/trpc';
import TagPills from '../../../common/TagPills';
import FactiiiSearchDropdown from '../../../UI/FactiiiSearchDropdown';
import Input from '../../../UI/Input';
type SortFilterKey = postDataSort | 'trending' | 'new' | 'popular';
type SortOption = {
  key: SortFilterKey;
  label: string;
};
type Props = {
  factiiiFilterOpen: boolean;
  setFactiiiFilterOpen: (open: boolean) => void;
  onManageGoals: () => void;
  showMediaSearch: boolean;
};
const FactiiiFilterDropdown: React.FC<Props> = t0 => {
  const $ = _c(82);
  const {
    factiiiFilterOpen,
    setFactiiiFilterOpen,
    onManageGoals,
    showMediaSearch
  } = t0;
  const {
    localPreferenceState,
    updatedLocalPreference
  } = useLocalPreference();
  const {
    updatePreferences,
    preferenceState
  } = usePreference(client);
  const {
    currentUser
  } = useUser();
  const {
    openAuthContinueModal
  } = useContext(AuthWidgetContext);
  const pathname = usePathname();
  const [searchQuery, setSearchQuery] = useState("");
  const [postModeDropdownOpen, setPostModeDropdownOpen] = useState(false);
  const [postModeOptionsOpen, setPostModeOptionsOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const panel = usePresence(factiiiFilterOpen, 300);
  const postMode = usePresence(postModeDropdownOpen, 200);
  const postOptions = usePresence(postModeOptionsOpen, 200);
  const sortMenu = usePresence(sortDropdownOpen, 200);
  const {
    getFactiiiIds
  } = useFactiiis(client);
  const dataFilterTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["home", "explore", "private"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const postModeOptions = t1;
  const postModeLabel = localPreferenceState.postModeType === "home" ? "For You" : localPreferenceState.postModeType === "private" ? "Local" : "Explore";
  let t2;
  if ($[1] !== localPreferenceState.displayMode) {
    t2 = localPreferenceState.displayMode === "data" ? [{
      key: postDataSort.DAILY,
      label: "Daily"
    }, {
      key: postDataSort.WEEKLY,
      label: "Weekly"
    }, {
      key: postDataSort.MONTHLY,
      label: "Monthly"
    }] : [{
      key: "trending",
      label: "Trending"
    }, {
      key: "new",
      label: "Newest"
    }, {
      key: "popular",
      label: "Popular"
    }];
    $[1] = localPreferenceState.displayMode;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const sortOptions = t2;
  const currentSortValue = localPreferenceState.displayMode === "data" ? localPreferenceState.postSortBy || postDataSort.DAILY : localPreferenceState.postFilterType || "trending";
  let t3;
  if ($[3] !== currentSortValue || $[4] !== sortOptions) {
    t3 = sortOptions.find(o => o.key === currentSortValue)?.label || sortOptions[0]?.label;
    $[3] = currentSortValue;
    $[4] = sortOptions;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const currentSortLabel = t3;
  let t4;
  if ($[6] !== updatedLocalPreference) {
    t4 = key => {
      if (key === "trending" || key === "new" || key === "popular") {
        updatedLocalPreference({
          postFilterType: key
        });
      } else {
        updatedLocalPreference({
          postSortBy: key
        });
      }
      setSortDropdownOpen(false);
    };
    $[6] = updatedLocalPreference;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleSortSelect = t4;
  const hasActiveFilters = localPreferenceState.filterBot || localPreferenceState.filterNew || localPreferenceState.showBoards || preferenceState.allowNSFWContent || localPreferenceState.dataFilter.length > 0 || localPreferenceState.selectedFactiiis.length > 0 || localPreferenceState.displayMode !== "posts";
  let t5;
  if ($[8] !== preferenceState.allowNSFWContent || $[9] !== updatePreferences || $[10] !== updatedLocalPreference) {
    t5 = () => {
      updatedLocalPreference({
        filterBot: false,
        filterNew: false,
        showBoards: false,
        displayMode: "posts",
        dataFilter: "",
        selectedFactiiis: []
      });
      if (preferenceState.allowNSFWContent) {
        updatePreferences(PreferencesEnum.allowNSFWContent);
      }
    };
    $[8] = preferenceState.allowNSFWContent;
    $[9] = updatePreferences;
    $[10] = updatedLocalPreference;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleResetFilters = t5;
  let t6;
  let t7;
  if ($[12] !== factiiiFilterOpen || $[13] !== setFactiiiFilterOpen) {
    t6 = () => {
      const handleClickOutside = event => {
        if (!factiiiFilterOpen || !dropdownRef.current) {
          return;
        }
        const target = event.target as Node;
        if (dropdownRef.current.contains(target)) {
          return;
        }
        const filterButton = document.querySelector("[data-id=\"factiii-filter\"]");
        if (filterButton && filterButton.contains(target)) {
          return;
        }
        setFactiiiFilterOpen(false);
      };
      document.addEventListener("click", handleClickOutside);
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    };
    t7 = [factiiiFilterOpen, setFactiiiFilterOpen];
    $[12] = factiiiFilterOpen;
    $[13] = setFactiiiFilterOpen;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useEffect(t6, t7);
  const preferSpaceSlug = useInstanceSpaceSlug();
  let t8;
  if ($[16] !== preferSpaceSlug || $[17] !== searchQuery) {
    t8 = ["factiii-search", searchQuery, preferSpaceSlug];
    $[16] = preferSpaceSlug;
    $[17] = searchQuery;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== localPreferenceState.selectedFactiiis || $[20] !== preferSpaceSlug || $[21] !== searchQuery) {
    t9 = async () => {
      const [, data] = await client.search.factiiis.query({
        query: searchQuery,
        preferSpaceSlug,
        ignoredIds: localPreferenceState.selectedFactiiis
      });
      if (data) {
        return data.factiiis;
      }
      return [];
    };
    $[19] = localPreferenceState.selectedFactiiis;
    $[20] = preferSpaceSlug;
    $[21] = searchQuery;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = searchQuery.length > 0;
  let t11;
  if ($[23] !== t10 || $[24] !== t8 || $[25] !== t9) {
    t11 = {
      queryKey: t8,
      queryFn: t9,
      enabled: t10,
      refetchOnWindowFocus: false
    };
    $[23] = t10;
    $[24] = t8;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const {
    data: t12
  } = useQuery(t11);
  let t13;
  if ($[27] !== t12) {
    t13 = t12 === undefined ? [] : t12;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const searchResults = t13;
  let t14;
  if ($[29] !== localPreferenceState.selectedFactiiis || $[30] !== setFactiiiFilterOpen || $[31] !== updatedLocalPreference) {
    t14 = factiii => {
      if (!localPreferenceState.selectedFactiiis.includes(factiii.id)) {
        const newSelectedFactiiis = [...localPreferenceState.selectedFactiiis, factiii.id];
        updatedLocalPreference({
          selectedFactiiis: newSelectedFactiiis
        });
        setFactiiiFilterOpen(false);
      }
      setSearchQuery("");
    };
    $[29] = localPreferenceState.selectedFactiiis;
    $[30] = setFactiiiFilterOpen;
    $[31] = updatedLocalPreference;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const addFactiii = t14;
  let t15;
  if ($[33] !== localPreferenceState.selectedFactiiis) {
    t15 = ["selected-factiiis", localPreferenceState.selectedFactiiis];
    $[33] = localPreferenceState.selectedFactiiis;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== getFactiiiIds || $[36] !== localPreferenceState.selectedFactiiis) {
    t16 = () => getFactiiiIds(localPreferenceState.selectedFactiiis);
    $[35] = getFactiiiIds;
    $[36] = localPreferenceState.selectedFactiiis;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t15 || $[39] !== t16) {
    t17 = {
      queryKey: t15,
      queryFn: t16
    };
    $[38] = t15;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  const {
    data: selectedFactiiis
  } = useQuery(t17);
  let t18;
  let t19;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => () => {
      if (dataFilterTimeoutRef.current) {
        clearTimeout(dataFilterTimeoutRef.current);
      }
    };
    t19 = [];
    $[41] = t18;
    $[42] = t19;
  } else {
    t18 = $[41];
    t19 = $[42];
  }
  useEffect(t18, t19);
  let t20;
  if ($[43] !== addFactiii || $[44] !== currentSortLabel || $[45] !== currentSortValue || $[46] !== currentUser || $[47] !== handleResetFilters || $[48] !== handleSortSelect || $[49] !== hasActiveFilters || $[50] !== localPreferenceState.dataFilter || $[51] !== localPreferenceState.displayMode || $[52] !== localPreferenceState.filterBot || $[53] !== localPreferenceState.filterNew || $[54] !== localPreferenceState.mediaSearch || $[55] !== localPreferenceState.postModeType || $[56] !== localPreferenceState.selectedFactiiis || $[57] !== localPreferenceState.showBoards || $[58] !== onManageGoals || $[59] !== openAuthContinueModal || $[60] !== panel.exiting || $[61] !== panel.rendered || $[62] !== pathname || $[63] !== postMode || $[64] !== postModeDropdownOpen || $[65] !== postModeLabel || $[66] !== postModeOptionsOpen || $[67] !== postOptions || $[68] !== preferenceState.allowNSFWContent || $[69] !== searchQuery || $[70] !== searchResults || $[71] !== selectedFactiiis || $[72] !== setFactiiiFilterOpen || $[73] !== showMediaSearch || $[74] !== sortDropdownOpen || $[75] !== sortMenu || $[76] !== sortOptions || $[77] !== updatePreferences || $[78] !== updatedLocalPreference) {
    t20 = panel.rendered && <div ref={dropdownRef} className={`absolute left-0 right-0 z-30 mt-2 w-full rounded-lg border border-ds-border-standard bg-background-secondary shadow-md [--enter-duration:300ms] [--enter-ease:cubic-bezier(0,0,0.2,1)] [--enter-y:-20px] [--exit-duration:300ms] [--exit-y:-20px] ${panel.exiting ? "anim-exit" : "anim-enter"}`}><div className="p-4"><div className="mb-3 flex items-center gap-2"><div className="relative z-50"><button onClick={e => {
              e.stopPropagation();
              setPostModeDropdownOpen(!postModeDropdownOpen);
            }} className="flex items-center rounded-lg border border-ds-border-standard bg-background-secondary px-3 py-2 text-sm text-textPrimary shadow-sm transition-all duration-300 hover:scale-[1.02] hover:border-primary hover:shadow-md active:scale-[0.98]"><div className="mr-2 text-textSecondary"><Settings size={14} /></div><span className="font-emphasis">{localPreferenceState.displayMode === "data" ? "Data Mode" : localPreferenceState.displayMode === "sortByFactiii" ? "Sort by Factiii" : "Posts Mode"}</span><div className={`ml-2 transition-transform duration-200 ${postModeDropdownOpen ? "rotate-180" : "rotate-0"}`}><ChevronDown size={16} /></div></button>{postMode.rendered && <div className={`absolute left-0 z-50 mt-2 w-44 rounded-lg border border-ds-border-standard bg-background-secondary shadow-xl [--enter-duration:200ms] [--enter-scale:0.95] [--enter-y:-10px] [--exit-duration:200ms] [--exit-scale:0.95] [--exit-y:-10px] ${postMode.exiting ? "anim-exit" : "anim-enter"}`}><div className="space-y-1 p-2"><button onClick={e_0 => {
                  e_0.stopPropagation();
                  updatedLocalPreference({
                    displayMode: "data"
                  });
                  setPostModeDropdownOpen(false);
                }} className={`anim-enter flex w-full items-center rounded-md px-3 py-2 text-left text-sm transition-all duration-200 [--enter-x:10px] ${localPreferenceState.displayMode === "data" ? "border border-primary/30 bg-primary/10 text-primary" : "text-textPrimary hover:bg-background-light"}`}><div className={`mr-3 ${localPreferenceState.displayMode === "data" ? "text-primary" : "text-textSecondary"}`}><BarChart3 size={16} /></div><span className="text-sm font-emphasis">Data Mode</span></button><button onClick={e_1 => {
                  e_1.stopPropagation();
                  updatedLocalPreference({
                    displayMode: "posts"
                  });
                  setPostModeDropdownOpen(false);
                }} className={`anim-enter flex w-full items-center rounded-md px-3 py-2 text-left text-sm transition-all duration-200 [--enter-x:10px] ${localPreferenceState.displayMode === "posts" ? "border border-primary/30 bg-primary/10 text-primary" : "text-textPrimary hover:bg-background-light"}`}><div className={`mr-3 ${localPreferenceState.displayMode === "posts" ? "text-primary" : "text-textSecondary"}`}><FileText size={16} /></div><span className="text-sm font-emphasis">Posts Mode</span></button><button onClick={e_2 => {
                  e_2.stopPropagation();
                  if (!currentUser.isAuthenticated) {
                    return openAuthContinueModal();
                  }
                  updatedLocalPreference({
                    displayMode: "sortByFactiii"
                  });
                  setPostModeDropdownOpen(false);
                }} className={`anim-enter flex w-full items-center rounded-md px-3 py-2 text-left text-sm transition-all duration-200 [--enter-x:10px] ${localPreferenceState.displayMode === "sortByFactiii" ? "border border-primary/30 bg-primary/10 text-primary" : "text-textPrimary hover:bg-background-light"}`}><div className={`mr-3 ${localPreferenceState.displayMode === "sortByFactiii" ? "text-primary" : "text-textSecondary"}`}><BarChart3 size={16} /></div><span className="text-sm font-emphasis">Sort by Factiii</span></button></div></div>}</div><div className="flex-1" />{(pathname === "/" || pathname === "/home") && <div className="relative"><button className="flex items-center rounded-lg border border-ds-border-standard bg-background-secondary px-3 py-2 text-sm text-textPrimary shadow-sm transition-all duration-300 hover:scale-[1.02] hover:border-primary hover:shadow-md active:scale-[0.98]" onClick={e_3 => {
              e_3.stopPropagation();
              setPostModeOptionsOpen(!postModeOptionsOpen);
            }}><span className="font-emphasis">{postModeLabel}</span><div className={`ml-2 transition-transform duration-200 ${postModeOptionsOpen ? "rotate-180" : "rotate-0"}`}><ChevronDown size={16} /></div></button>{postOptions.rendered && <div className={`absolute right-0 z-50 mt-2 w-40 rounded-lg border border-ds-border-standard bg-background-secondary shadow-xl [--enter-duration:200ms] [--enter-scale:0.95] [--enter-y:-10px] [--exit-duration:200ms] [--exit-scale:0.95] [--exit-y:-10px] ${postOptions.exiting ? "anim-exit" : "anim-enter"}`}><div className="p-2">{postModeOptions.map(mode => {
                  if (mode === localPreferenceState.postModeType) {
                    return null;
                  }
                  const label = mode === "home" ? "For You" : mode === "private" ? "Local" : "Explore";
                  return <button key={mode} className="anim-enter flex w-full items-center rounded-md px-3 py-2 text-left text-sm transition-colors [--enter-x:-10px] hover:bg-background-light" onClick={e_4 => {
                    e_4.stopPropagation();
                    if ((mode === "home" || mode === "private") && !currentUser.isAuthenticated) {
                      return openAuthContinueModal();
                    }
                    updatedLocalPreference({
                      postModeType: mode,
                      displayMode: spaceConfig.layoutType === DeploymentLayout.Space ? "data" : localPreferenceState.displayMode
                    });
                    setPostModeOptionsOpen(false);
                  }}><span className="font-emphasis">{label}</span></button>;
                })}</div></div>}</div>}<div className="relative"><button className="flex items-center rounded-lg border border-ds-border-standard bg-background-secondary px-3 py-2 text-sm text-textPrimary shadow-sm transition-all duration-300 hover:scale-[1.02] hover:border-primary hover:shadow-md active:scale-[0.98]" onClick={e_5 => {
              e_5.stopPropagation();
              setSortDropdownOpen(!sortDropdownOpen);
            }}><span className="font-emphasis">{currentSortLabel}</span><div className={`ml-2 transition-transform duration-200 ${sortDropdownOpen ? "rotate-180" : "rotate-0"}`}><ChevronDown size={16} /></div></button>{sortMenu.rendered && <div className={`absolute right-0 z-50 mt-2 w-40 rounded-lg border border-ds-border-standard bg-background-secondary shadow-xl [--enter-duration:200ms] [--enter-scale:0.95] [--enter-y:-10px] [--exit-duration:200ms] [--exit-scale:0.95] [--exit-y:-10px] ${sortMenu.exiting ? "anim-exit" : "anim-enter"}`}><div className="p-2">{sortOptions.map((option, index) => <button key={option.key} style={animDelay(index * 50)} className={`anim-enter flex w-full items-center rounded-md px-3 py-2 text-left text-sm transition-colors [--enter-x:-10px] ${currentSortValue === option.key ? "bg-primary/10 text-primary" : "hover:bg-background-light"}`} onClick={e_6 => {
                  e_6.stopPropagation();
                  handleSortSelect(option.key);
                }}><span className="font-emphasis">{option.label}</span></button>)}</div></div>}</div></div><div className="mb-3 flex items-center justify-between"><div className="flex flex-wrap gap-2"><button onClick={e_7 => {
              e_7.stopPropagation();
              if (!currentUser.isAuthenticated) {
                return openAuthContinueModal();
              }
              updatePreferences(PreferencesEnum.allowNSFWContent);
            }} className={`flex items-center rounded-lg px-3 py-2 text-sm transition-all duration-200 ${preferenceState.allowNSFWContent ? "border border-primary/30 bg-primary/10 text-primary" : "border border-ds-border-standard bg-background-secondary text-textPrimary hover:bg-background-light"}`}><span className="font-emphasis">NSFW</span></button>{spaceConfig.layoutType !== DeploymentLayout.Space && <button onClick={e_8 => {
              e_8.stopPropagation();
              if (!currentUser.isAuthenticated) {
                return openAuthContinueModal();
              }
              updatedLocalPreference({
                filterBot: !localPreferenceState.filterBot
              });
            }} className={`flex items-center rounded-lg px-3 py-2 text-sm transition-all duration-200 ${localPreferenceState.filterBot ? "border border-primary/30 bg-primary/10 text-primary" : "border border-ds-border-standard bg-background-secondary text-textPrimary hover:bg-background-light"}`}><span className="font-emphasis">Bots</span></button>}{spaceConfig.layoutType !== DeploymentLayout.Space && <button onClick={e_9 => {
              e_9.stopPropagation();
              if (!currentUser.isAuthenticated) {
                return openAuthContinueModal();
              }
              updatedLocalPreference({
                filterNew: !localPreferenceState.filterNew
              });
            }} className={`flex items-center rounded-lg px-3 py-2 text-sm transition-all duration-200 ${localPreferenceState.filterNew ? "border border-primary/30 bg-primary/10 text-primary" : "border border-ds-border-standard bg-background-secondary text-textPrimary hover:bg-background-light"}`}><span className="font-emphasis">New accounts</span></button>}{spaceConfig.layoutType !== DeploymentLayout.Space && <button onClick={e_10 => {
              e_10.stopPropagation();
              if (!currentUser.isAuthenticated) {
                return openAuthContinueModal();
              }
              updatedLocalPreference({
                showBoards: !localPreferenceState.showBoards
              });
            }} className={`flex items-center rounded-lg px-3 py-2 text-sm transition-all duration-200 ${localPreferenceState.showBoards ? "border border-primary/30 bg-primary/10 text-primary" : "border border-ds-border-standard bg-background-secondary text-textPrimary hover:bg-background-light"}`}><span className="font-emphasis">Boards</span></button>}</div><div className="flex items-center gap-2">{hasActiveFilters && <button onClick={e_11 => {
              e_11.stopPropagation();
              handleResetFilters();
            }} className="flex items-center gap-1.5 rounded-lg border border-ds-border-standard bg-background-secondary px-3 py-2 text-sm text-textSecondary transition-all duration-200 hover:bg-background-light hover:text-textPrimary" title="Reset all filters to default"><RotateCcw size={14} /><span className="font-emphasis">Reset</span></button>}<button onClick={e_12 => {
              e_12.stopPropagation();
              if (!currentUser.isAuthenticated) {
                return openAuthContinueModal();
              }
              onManageGoals();
              setFactiiiFilterOpen(false);
            }} className="flex items-center rounded-lg border border-ds-border-standard bg-background-secondary px-3 py-2 text-sm text-textPrimary transition-all duration-200 hover:bg-background-light"><span className="font-emphasis">Goals</span></button></div></div><div className="mb-3"><div className="relative"><Input value={localPreferenceState.dataFilter} onChange={value => {
              updatedLocalPreference({
                dataFilter: value
              });
              if (dataFilterTimeoutRef.current) {
                clearTimeout(dataFilterTimeoutRef.current);
              }
              if (value.length > 0) {
                dataFilterTimeoutRef.current = setTimeout(() => {
                  setFactiiiFilterOpen(false);
                }, 300);
              }
            }} label="Search in factiii data..." className="w-full rounded-lg text-sm" icons={[<Search size={18} key="search" />]} /></div></div>{showMediaSearch && <div className="mb-3"><div className="relative"><Input value={localPreferenceState.mediaSearch} onChange={value_0 => updatedLocalPreference({
              mediaSearch: value_0,
              displayMode: value_0 ? "mediaSearch" : "posts"
            })} label="Search media by content..." className="w-full rounded-lg text-sm" icons={[<Search size={18} key="media-search" />]} /></div></div>}<FactiiiSearchDropdown searchValue={searchQuery} onSearchChange={setSearchQuery} searchResults={searchResults} onSelect={factiii_0 => addFactiii(factiii_0)} placeholder="Search factiiis..." />{localPreferenceState.selectedFactiiis.length > 0 && selectedFactiiis && <div className="mt-3"><TagPills tags={selectedFactiiis.map(_temp)} onRemove={factiii_2 => {
            const newSelectedFactiiis_0 = localPreferenceState.selectedFactiiis.filter(id => id !== factiii_2.id);
            updatedLocalPreference({
              selectedFactiiis: newSelectedFactiiis_0
            });
          }} showContainer={false} /></div>}</div></div>;
    $[43] = addFactiii;
    $[44] = currentSortLabel;
    $[45] = currentSortValue;
    $[46] = currentUser;
    $[47] = handleResetFilters;
    $[48] = handleSortSelect;
    $[49] = hasActiveFilters;
    $[50] = localPreferenceState.dataFilter;
    $[51] = localPreferenceState.displayMode;
    $[52] = localPreferenceState.filterBot;
    $[53] = localPreferenceState.filterNew;
    $[54] = localPreferenceState.mediaSearch;
    $[55] = localPreferenceState.postModeType;
    $[56] = localPreferenceState.selectedFactiiis;
    $[57] = localPreferenceState.showBoards;
    $[58] = onManageGoals;
    $[59] = openAuthContinueModal;
    $[60] = panel.exiting;
    $[61] = panel.rendered;
    $[62] = pathname;
    $[63] = postMode;
    $[64] = postModeDropdownOpen;
    $[65] = postModeLabel;
    $[66] = postModeOptionsOpen;
    $[67] = postOptions;
    $[68] = preferenceState.allowNSFWContent;
    $[69] = searchQuery;
    $[70] = searchResults;
    $[71] = selectedFactiiis;
    $[72] = setFactiiiFilterOpen;
    $[73] = showMediaSearch;
    $[74] = sortDropdownOpen;
    $[75] = sortMenu;
    $[76] = sortOptions;
    $[77] = updatePreferences;
    $[78] = updatedLocalPreference;
    $[79] = t20;
  } else {
    t20 = $[79];
  }
  let t21;
  if ($[80] !== t20) {
    t21 = <>{t20}</>;
    $[80] = t20;
    $[81] = t21;
  } else {
    t21 = $[81];
  }
  return t21;
};
export default FactiiiFilterDropdown;
function _temp(factiii_1) {
  return {
    id: factiii_1.id,
    type: "factiii",
    name: factiii_1.name,
    slug: factiii_1.slug,
    avatarKey: factiii_1.avatarKey,
    spaceSlug: factiii_1.spaceSlug
  };
}